import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MessageService, ConfirmationService } from 'primeng/api';

import { TableModule, TablePageEvent } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { CheckboxModule } from 'primeng/checkbox';
import { DnPrintService } from '../../core/dn-print.service';
import * as XLSX from 'xlsx';
import { environment } from '../../../environments/environment';
import { ApiService, Company, FuelDispatch } from '../../core/api.service';

@Component({
    selector: 'app-dashboard',
    standalone: true,
    imports: [CommonModule, FormsModule, TableModule, ButtonModule, SelectModule, InputTextModule, InputNumberModule, DatePickerModule, DialogModule, TagModule, ToastModule, ConfirmDialogModule, IconFieldModule, InputIconModule, CheckboxModule],
    providers: [MessageService, ConfirmationService],
    template: `
        <p-toast></p-toast>
        <p-confirmDialog></p-confirmDialog>

        <!-- Summary cards -->
        <div class="grid grid-cols-12 gap-8 mb-8">
            <div class="col-span-12 lg:col-span-6 xl:col-span-3">
                <div class="card mb-0">
                    <div class="flex justify-between mb-4">
                        <div>
                            <span class="block text-muted-color font-medium mb-4">Total Records</span>
                            <div class="text-surface-900 dark:text-surface-0 font-medium text-xl">{{ total }}</div>
                        </div>
                        <div class="flex items-center justify-center bg-blue-100 dark:bg-blue-400/10 rounded-border" style="width: 2.5rem; height: 2.5rem">
                            <i class="pi pi-database text-blue-500 text-xl"></i>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-span-12 lg:col-span-6 xl:col-span-3">
                <div class="card mb-0">
                    <div class="flex justify-between mb-4">
                        <div>
                            <span class="block text-muted-color font-medium mb-4">Companies</span>
                            <div class="text-surface-900 dark:text-surface-0 font-medium text-xl">{{ companies.length }}</div>
                        </div>
                        <div class="flex items-center justify-center bg-orange-100 dark:bg-orange-400/10 rounded-border" style="width: 2.5rem; height: 2.5rem">
                            <i class="pi pi-building text-orange-500 text-xl"></i>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-span-12 lg:col-span-6 xl:col-span-3">
                <div class="card mb-0">
                    <div class="flex justify-between mb-4">
                        <div>
                            <span class="block text-muted-color font-medium mb-4">Selected</span>
                            <div class="text-surface-900 dark:text-surface-0 font-medium text-xl">{{ selectedIds.size }}</div>
                        </div>
                        <div class="flex items-center justify-center bg-purple-100 dark:bg-purple-400/10 rounded-border" style="width: 2.5rem; height: 2.5rem">
                            <i class="pi pi-check-square text-purple-500 text-xl"></i>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Upload card -->
        <div class="card mb-8">
            <div class="font-semibold text-xl mb-4"><i class="pi pi-upload mr-2"></i>Excel Upload</div>
            <div class="flex flex-wrap gap-4 items-end">
                <div class="flex flex-col gap-2">
                    <label class="font-medium text-sm">Company</label>
                    <p-select [options]="companies" [(ngModel)]="selectedCompanyId" optionLabel="name" optionValue="id" placeholder="Select company" [filter]="true" styleClass="w-72"></p-select>
                </div>
                <div class="flex flex-col gap-2">
                    <label class="font-medium text-sm">Excel File (.xlsx)</label>
                    <input #fileInput type="file" accept=".xlsx,.xls" (change)="onFileSelect($event)" class="p-2 border border-surface rounded-border" />
                </div>
                <div>
                    <p-button label="Upload" icon="pi pi-cloud-upload" [loading]="uploading" [disabled]="!fileValid" (onClick)="onUpload()"></p-button>
                </div>
            </div>
            <small *ngIf="selectedFile && fileValid" class="text-green-600 mt-3 block"> <i class="pi pi-check-circle mr-1"></i>{{ selectedFile.name }} — valid file </small>
            <small *ngIf="selectedFile && !fileValid" class="text-red-500 mt-3 block"> <i class="pi pi-times-circle mr-1"></i>{{ fileError }} </small>
        </div>

        <!-- Table card -->
        <div class="card">
            <!-- filter row -->
            <div class="flex flex-wrap gap-4 items-end mb-4">
                <div class="flex flex-col gap-2">
                    <label class="font-medium text-sm">Filter by Company</label>
                    <p-select [options]="companies" [(ngModel)]="filterCompanyId" optionLabel="name" optionValue="id" placeholder="All Companies" [showClear]="true" (onChange)="onFilterChange()" styleClass="w-64"></p-select>
                </div>
                <div class="flex flex-col gap-2">
                    <label class="font-medium text-sm">Search</label>
                    <p-iconfield iconPosition="left">
                        <p-inputicon styleClass="pi pi-search" />
                        <input type="text" pInputText [(ngModel)]="search" (keyup.enter)="onSearch()" placeholder="Location / Product / DN / TL" class="w-72" />
                    </p-iconfield>
                </div>
                <div>
                    <p-button icon="pi pi-search" label="Search" (onClick)="onSearch()" severity="secondary"></p-button>
                </div>
            </div>

            <!-- selection toolbar -->
            <div class="flex flex-wrap gap-2 items-center mb-4 p-3 rounded-border" style="background: var(--surface-100)">
                <span class="font-medium text-sm mr-2">{{ selectedIds.size }} selected</span>
                <p-button label="Select All Records" icon="pi pi-list-check" size="small" severity="secondary" (onClick)="selectAllRecords()"></p-button>
                <p-button label="Clear" icon="pi pi-times" size="small" severity="secondary" [outlined]="true" (onClick)="clearSelection()" [disabled]="selectedIds.size === 0"></p-button>
                <p-button label="Delete Selected" icon="pi pi-trash" size="small" severity="danger" (onClick)="deleteSelected()" [disabled]="selectedIds.size === 0"></p-button>
                <p-button label="Print Selected" icon="pi pi-print" size="small" (onClick)="printSelected()" [disabled]="selectedIds.size === 0"></p-button>
                <p-button label="Download PDF" icon="pi pi-file-pdf" size="small" severity="help" (onClick)="downloadPdfSelected()" [disabled]="selectedIds.size === 0"></p-button>
            </div>

            <p-table
                [value]="records"
                [lazy]="true"
                (onLazyLoad)="onPage($any($event))"
                [paginator]="true"
                [rows]="pageSize"
                [totalRecords]="total"
                [loading]="loading"
                [rowsPerPageOptions]="[10, 20, 50]"
                [scrollable]="true"
                scrollHeight="500px"
                [showCurrentPageReport]="true"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} records"
                styleClass="p-datatable-sm p-datatable-striped"
                [tableStyle]="{ 'min-width': '90rem' }"
            >
                <ng-template pTemplate="header">
                    <tr>
                        <th style="width:3rem" class="text-center">
                            <p-checkbox [binary]="true" [ngModel]="isPageAllSelected()" (onChange)="togglePage($event)"></p-checkbox>
                        </th>
                        <th style="width:4rem">Sr</th>
                        <th>To Location</th>
                        <th>Product</th>
                        <th>From</th>
                        <th>DN #</th>
                        <th>Indent #</th>
                        <th>TL #</th>
                        <th>Doc Date</th>
                        <th>Contractor</th>
                        <th>Qty</th>
                        <th>Ambiant L</th>
                        <th>Metric Tons</th>
                        <th style="width:8rem">Actions</th>
                    </tr>
                </ng-template>

                <ng-template pTemplate="body" let-row>
                    <tr [class.bg-primary-50]="selectedIds.has(row.id)">
                        <td class="text-center">
                            <p-checkbox [binary]="true" [ngModel]="selectedIds.has(row.id)" (onChange)="toggleRow(row.id)"></p-checkbox>
                        </td>
                        <td>{{ row.sr }}</td>
                        <td>{{ row.toLocation }}</td>
                        <td>
                            <p-tag *ngIf="row.product" [value]="row.product" [severity]="row.product === 'Petrol' ? 'success' : 'warn'"></p-tag>
                        </td>
                        <td>{{ row.fromLocation }}</td>
                        <td>{{ row.dnNumber }}</td>
                        <td>{{ row.indentNumber }}</td>
                        <td>{{ row.tlNumber }}</td>
                        <td>{{ row.documentDate ? (row.documentDate | date: 'dd-MMM-yyyy') : '' }}</td>
                        <td>{{ row.carriageContractor }}</td>
                        <td>{{ row.quantity }}</td>
                        <td>{{ row.ambiantLiters }}</td>
                        <td>{{ row.metricTons | number: '1.0-2' }}</td>
                        <td>
                            <p-button icon="pi pi-pencil" severity="info" [text]="true" (onClick)="openEdit(row)"></p-button>
                            <p-button icon="pi pi-trash" severity="danger" [text]="true" (onClick)="confirmDelete(row)"></p-button>
                        </td>
                    </tr>
                </ng-template>

                <ng-template pTemplate="emptymessage">
                    <tr>
                        <td colspan="14" class="text-center p-8 text-muted-color"><i class="pi pi-inbox text-2xl block mb-2"></i>No records found. Upload an Excel file.</td>
                    </tr>
                </ng-template>
            </p-table>
        </div>

        <!-- Edit dialog -->
        <p-dialog header="Edit Record" [(visible)]="editDialog" [modal]="true" [style]="{ width: '850px' }" [draggable]="false" [maximizable]="true">
            <div class="font-semibold text-lg mb-3 mt-1 text-primary">General Info</div>
            <div class="grid grid-cols-2 gap-4 mb-6">
                <div class="flex flex-col gap-2"><label class="text-sm font-medium">To Location</label><input pInputText [(ngModel)]="editItem.toLocation" /></div>
                <div class="flex flex-col gap-2"><label class="text-sm font-medium">Product</label><input pInputText [(ngModel)]="editItem.product" /></div>
                <div class="flex flex-col gap-2"><label class="text-sm font-medium">From</label><input pInputText [(ngModel)]="editItem.fromLocation" /></div>
                <div class="flex flex-col gap-2"><label class="text-sm font-medium">Carriage Contractor</label><input pInputText [(ngModel)]="editItem.carriageContractor" /></div>
                <div class="flex flex-col gap-2"><label class="text-sm font-medium">DN #</label><input pInputText [(ngModel)]="editItem.dnNumber" /></div>
                <div class="flex flex-col gap-2"><label class="text-sm font-medium">Indent #</label><input pInputText [(ngModel)]="editItem.indentNumber" /></div>
                <div class="flex flex-col gap-2"><label class="text-sm font-medium">TL #</label><input pInputText [(ngModel)]="editItem.tlNumber" /></div>
                <div class="flex flex-col gap-2"><label class="text-sm font-medium">Document Date</label><p-datepicker [(ngModel)]="editDate" dateFormat="dd-M-yy" [showIcon]="true" appendTo="body"></p-datepicker></div>
                <div class="flex flex-col gap-2 col-span-2"><label class="text-sm font-medium">Address</label><input pInputText [(ngModel)]="editItem.address" /></div>
            </div>

            <div class="font-semibold text-lg mb-3 text-primary">Quantities</div>
            <div class="grid grid-cols-3 gap-4 mb-6">
                <div class="flex flex-col gap-2"><label class="text-sm font-medium">Quantity</label><p-inputnumber [(ngModel)]="editItem.quantity" [useGrouping]="false"></p-inputnumber></div>
                <div class="flex flex-col gap-2"><label class="text-sm font-medium">Ambiant Liters</label><p-inputnumber [(ngModel)]="editItem.ambiantLiters" [useGrouping]="false"></p-inputnumber></div>
                <div class="flex flex-col gap-2"><label class="text-sm font-medium">Metric Tons</label><p-inputnumber [(ngModel)]="editItem.metricTons" [useGrouping]="false" [minFractionDigits]="0" [maxFractionDigits]="4"></p-inputnumber></div>
            </div>

            <div class="font-semibold text-lg mb-3 text-primary">Tank Receiving Dip (RD)</div>
            <div class="grid grid-cols-5 gap-3 mb-6">
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Tank 1</label><p-inputnumber [(ngModel)]="editItem.tank1RD" [useGrouping]="false"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Tank 2</label><p-inputnumber [(ngModel)]="editItem.tank2RD" [useGrouping]="false"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Tank 3</label><p-inputnumber [(ngModel)]="editItem.tank3RD" [useGrouping]="false"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Tank 4</label><p-inputnumber [(ngModel)]="editItem.tank4RD" [useGrouping]="false"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Tank 5</label><p-inputnumber [(ngModel)]="editItem.tank5RD" [useGrouping]="false"></p-inputnumber></div>
            </div>

            <div class="font-semibold text-lg mb-3 text-primary">Tank Physical Dip (PD)</div>
            <div class="grid grid-cols-5 gap-3 mb-6">
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Tank 1</label><p-inputnumber [(ngModel)]="editItem.tank1PD" [useGrouping]="false"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Tank 2</label><p-inputnumber [(ngModel)]="editItem.tank2PD" [useGrouping]="false"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Tank 3</label><p-inputnumber [(ngModel)]="editItem.tank3PD" [useGrouping]="false"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Tank 4</label><p-inputnumber [(ngModel)]="editItem.tank4PD" [useGrouping]="false"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Tank 5</label><p-inputnumber [(ngModel)]="editItem.tank5PD" [useGrouping]="false"></p-inputnumber></div>
            </div>

            <div class="font-semibold text-lg mb-3 text-primary">Tank Quantity Dip (QD)</div>
            <div class="grid grid-cols-5 gap-3 mb-2">
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Tank 1</label><p-inputnumber [(ngModel)]="editItem.tank1QD" [useGrouping]="false"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Tank 2</label><p-inputnumber [(ngModel)]="editItem.tank2QD" [useGrouping]="false"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Tank 3</label><p-inputnumber [(ngModel)]="editItem.tank3QD" [useGrouping]="false"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Tank 4</label><p-inputnumber [(ngModel)]="editItem.tank4QD" [useGrouping]="false"></p-inputnumber></div>
                <div class="flex flex-col gap-1"><label class="text-xs font-medium">Tank 5</label><p-inputnumber [(ngModel)]="editItem.tank5QD" [useGrouping]="false"></p-inputnumber></div>
            </div>

            <ng-template pTemplate="footer">
                <p-button label="Cancel" icon="pi pi-times" severity="secondary" [text]="true" (onClick)="editDialog = false"></p-button>
                <p-button label="Save" icon="pi pi-check" (onClick)="saveEdit()"></p-button>
            </ng-template>
        </p-dialog>
    `
})
export class Dashboard implements OnInit {
    companies: Company[] = [];
    selectedCompanyId: number | null = null;
    filterCompanyId: number | null = null;

    selectedFile: File | null = null;
    uploading = false;
    fileValid = false;
    fileError = '';

    records: FuelDispatch[] = [];
    total = 0;
    page = 1;
    pageSize = 10;
    loading = false;
    search = '';

    // selection
    selectedIds = new Set<number>();

    editDialog = false;
    editItem: FuelDispatch = {} as FuelDispatch;
    editDate: Date | null = null;

    private expectedHeaders = [
        'Sr.',
        'To LOcation',
        'Product',
        'From',
        'Address',
        'DN #',
        'Indent Number',
        'TL #',
        'Document Date',
        'Carriage Contractor',
        'Quantity',
        'Ambiant Liters',
        'Metric Tons',
        'Tank 1 RD',
        'Tank 2 RD',
        'Tank 3 RD',
        'Tank 4 RD',
        'Tank 5 RD',
        'Tank 1 PD',
        'Tank 2 PD',
        'Tank 3 PD',
        'Tank 4 PD',
        'Tank 5 PD',
        'Tank 1 QD',
        'Tank 2 QD',
        'Tank 3 QD',
        'Tank 4 QD',
        'Tank 5 QD'
    ];

    constructor(
        private api: ApiService,
        private router: Router,
        private messageService: MessageService,
        private confirmationService: ConfirmationService,
        private cd: ChangeDetectorRef,
        private dnPrint: DnPrintService
    ) {}

    ngOnInit(): void {
        this.loadCompanies();
        this.loadData();
    }

    loadCompanies(): void {
        this.api.getCompanies().subscribe({
            next: (data) => {
                this.companies = data;
                this.cd.detectChanges();
            },
            error: () => this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load companies.' })
        });
    }

    private norm(s: any): string {
        return String(s ?? '')
            .trim()
            .toLowerCase()
            .replace(/\s+/g, ' ');
    }

    onFileSelect(event: Event): void {
        const input = event.target as HTMLInputElement;
        this.selectedFile = input.files && input.files.length ? input.files[0] : null;
        this.fileValid = false;
        this.fileError = '';
        if (!this.selectedFile) {
            this.cd.detectChanges();
            return;
        }

        const reader = new FileReader();
        reader.onload = (e: any) => {
            try {
                const wb = XLSX.read(e.target.result, { type: 'array' });
                const ws = wb.Sheets[wb.SheetNames[0]];
                const rows: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false });
                const headerRow = rows && rows.length > 0 ? rows[0] : [];

                const actual = headerRow.map((h) => this.norm(h)).filter((h) => h !== '');
                const expected = this.expectedHeaders.map((h) => this.norm(h));

                if (actual.length < expected.length) {
                    this.fileValid = false;
                    this.fileError = `Invalid file: expected ${expected.length} columns but found ${actual.length}. Please use the correct template.`;
                    this.cd.detectChanges();
                    return;
                }

                const mismatches: string[] = [];
                for (let i = 0; i < expected.length; i++) {
                    if (actual[i] !== expected[i]) {
                        mismatches.push(`Column ${i + 1}: expected "${this.expectedHeaders[i]}", found "${headerRow[i] ?? '(empty)'}"`);
                    }
                }

                if (mismatches.length > 0) {
                    this.fileValid = false;
                    this.fileError = 'Invalid file format. ' + mismatches.slice(0, 3).join('; ') + (mismatches.length > 3 ? ' ...' : '');
                    this.messageService.add({ severity: 'error', summary: 'Invalid File', detail: 'Excel columns do not match the required format.' });
                    this.cd.detectChanges();
                    return;
                }

                this.fileValid = true;
                this.fileError = '';
                this.cd.detectChanges();
            } catch (err) {
                this.fileValid = false;
                this.fileError = 'Could not read the file. Make sure it is a valid Excel file.';
                this.cd.detectChanges();
            }
        };
        reader.readAsArrayBuffer(this.selectedFile);
    }

    onUpload(): void {
        if (!this.selectedCompanyId) {
            this.messageService.add({ severity: 'warn', summary: 'Wait', detail: 'Please select a company first.' });
            return;
        }
        if (!this.selectedFile) {
            this.messageService.add({ severity: 'warn', summary: 'Wait', detail: 'Please select an Excel file.' });
            return;
        }
        if (!this.fileValid) {
            this.messageService.add({ severity: 'error', summary: 'Invalid File', detail: this.fileError || 'File format does not match the required template.' });
            return;
        }

        this.uploading = true;
        this.api.uploadExcel(this.selectedCompanyId, this.selectedFile).subscribe({
            next: (res: any) => {
                this.uploading = false;
                this.selectedFile = null;
                this.fileValid = false;
                this.messageService.add({ severity: 'success', summary: 'Done', detail: res?.message || 'Uploaded successfully.' });
                this.filterCompanyId = this.selectedCompanyId;
                this.page = 1;
                this.clearSelection();
                this.loadData();
                this.cd.detectChanges();
            },
            error: (err) => {
                this.uploading = false;
                const detail = err?.error?.message || 'Upload failed.';
                this.messageService.add({ severity: 'error', summary: 'Error', detail });
                this.cd.detectChanges();
            }
        });
    }

    loadData(): void {
        this.loading = true;
        this.api.getList(this.filterCompanyId, this.page, this.pageSize, this.search).subscribe({
            next: (res) => {
                this.records = res.data;
                this.total = res.total;
                this.loading = false;
                this.cd.detectChanges();
            },
            error: () => {
                this.loading = false;
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load data.' });
                this.cd.detectChanges();
            }
        });
    }

    onPage(event: TablePageEvent): void {
        this.page = Math.floor(event.first / event.rows) + 1;
        this.pageSize = event.rows;
        this.loadData();
    }

    onFilterChange(): void {
        this.page = 1;
        this.clearSelection();
        this.loadData();
    }
    onSearch(): void {
        this.page = 1;
        this.clearSelection();
        this.loadData();
    }

    // ---------- SELECTION ----------
    toggleRow(id: number): void {
        if (this.selectedIds.has(id)) this.selectedIds.delete(id);
        else this.selectedIds.add(id);
        this.cd.detectChanges();
    }

    isPageAllSelected(): boolean {
        return this.records.length > 0 && this.records.every((r) => this.selectedIds.has(r.id));
    }

    togglePage(event: any): void {
        const checked = event?.checked ?? !this.isPageAllSelected();
        if (checked) {
            this.records.forEach((r) => this.selectedIds.add(r.id));
        } else {
            this.records.forEach((r) => this.selectedIds.delete(r.id));
        }
        this.cd.detectChanges();
    }

    selectAllRecords(): void {
        // current filter/search ke saare IDs backend se
        this.api.getAllIds(this.filterCompanyId, this.search).subscribe({
            next: (ids) => {
                ids.forEach((id) => this.selectedIds.add(id));
                this.messageService.add({ severity: 'info', summary: 'Selected', detail: `${ids.length} record(s) selected.` });
                this.cd.detectChanges();
            },
            error: () => this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not select all records.' })
        });
    }

    clearSelection(): void {
        this.selectedIds.clear();
        this.cd.detectChanges();
    }

    deleteSelected(): void {
        if (this.selectedIds.size === 0) return;
        const ids = Array.from(this.selectedIds);

        this.confirmationService.confirm({
            message: `Delete ${ids.length} selected record(s)? This cannot be undone.`,
            header: 'Confirm Bulk Delete',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Yes, delete all',
            rejectLabel: 'Cancel',
            acceptButtonStyleClass: 'p-button-danger',
            accept: () => {
                this.api.bulkDelete(ids).subscribe({
                    next: (res: any) => {
                        this.messageService.add({ severity: 'success', summary: 'Deleted', detail: res?.message || 'Records deleted.' });
                        this.clearSelection();
                        this.loadData();
                        this.cd.detectChanges();
                    },
                    error: () => {
                        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Bulk delete failed.' });
                        this.cd.detectChanges();
                    }
                });
            }
        });
    }

    // ---------- PRINT ----------
    // ---------- PRINT (DN slip) ----------
    printSelected(): void {
        this.getSelectedRows((rows, logoUrl, companyName) => this.dnPrint.print(rows, logoUrl, companyName));
    }

    // ---------- DOWNLOAD PDF (DN slip, A4) ----------
    downloadPdfSelected(): void {
        this.getSelectedRows(async (rows, logoUrl, companyName) => {
            this.messageService.add({ severity: 'info', summary: 'Please wait', detail: 'Generating PDF...' });
            await this.dnPrint.downloadPdf(rows, logoUrl, companyName);
        });
    }

    // selected ke poore records laao (har page se)
    private getSelectedRows(cb: (rows: FuelDispatch[], logoUrl: string, companyName: string) => void): void {
        if (this.selectedIds.size === 0) return;
        this.api.getList(this.filterCompanyId, 1, 100000, this.search).subscribe({
            next: (res) => {
                const rows = res.data.filter((r) => this.selectedIds.has(r.id));
                const companyId = rows.length > 0 ? rows[0].companyId : null;
                const company = this.companies.find((c) => c.id === companyId);
                const logoUrl = company && company.logoPath ? environment.serverUrl + company.logoPath : '';
                const companyName = company ? company.name : '';
                cb(rows, logoUrl, companyName);
            },
            error: () => this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not load records.' })
        });
    }

    // ---------- EDIT / DELETE ----------
    openEdit(item: FuelDispatch): void {
        this.editItem = { ...item };
        this.editDate = item.documentDate ? new Date(item.documentDate) : null;
        this.editDialog = true;
    }

    saveEdit(): void {
        this.editItem.documentDate = this.editDate ? this.editDate.toISOString() : undefined;
        this.api.update(this.editItem.id, this.editItem).subscribe({
            next: () => {
                this.editDialog = false;
                this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Record updated.' });
                this.loadData();
                this.cd.detectChanges();
            },
            error: () => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Update failed.' });
                this.cd.detectChanges();
            }
        });
    }

    confirmDelete(item: FuelDispatch): void {
        this.confirmationService.confirm({
            message: `Delete "${item.toLocation || 'this record'}"?`,
            header: 'Confirm Delete',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Yes, delete',
            rejectLabel: 'Cancel',
            acceptButtonStyleClass: 'p-button-danger',
            accept: () => {
                this.api.delete(item.id).subscribe({
                    next: () => {
                        this.messageService.add({ severity: 'success', summary: 'Deleted', detail: 'Record deleted.' });
                        this.selectedIds.delete(item.id);
                        this.loadData();
                        this.cd.detectChanges();
                    },
                    error: () => {
                        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Delete failed.' });
                        this.cd.detectChanges();
                    }
                });
            }
        });
    }
}
